import { useMemoizedFn } from 'ahooks';
import { Tooltip } from 'antd';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { BtnBackIcon, BtnNextIcon } from 'components/Icons';
import { RevxButton } from 'components/RevxButton';
import { findIndex, findLastIndex } from 'lodash-es';
import { useMemo } from 'react';
import { StepTypeEnum } from 'utils/jobConfig';

type Props = {
  stepAction?: () => Promise<Boolean> | undefined;
  disabled?: boolean;
  nextBtnTip?: string;
};

export const StepButtons = (props: Props) => {
  const { stepAction, disabled, nextBtnTip } = props;
  const dispatch = useAppDispatch();
  const partDropdownList = useAppSelector(
    (state) => state.jobConfig.partDropdownList
  );
  const step = useAppSelector((state) => state.jobConfig.step);
  const stepInfos = useAppSelector((state) => state.jobConfig.stepInfos);
  const totalStep = useAppSelector((state) => state.jobConfig.stepInfos.length);

  const prevDisabled = useMemo(() => {
    return step < 2 ? step < 2 : disabled;
  }, [step, disabled]);

  const nextHide = useMemo(() => {
    return step + 1 >= totalStep && step > 0;
  }, [step, totalStep]);

  const nextDisabled = useMemo(() => {
    return disabled !== undefined ? disabled : step + 1 >= totalStep;
  }, [disabled, step, totalStep]);

  const prevClick = useMemoizedFn(() => {
    dispatch.jobConfig.goToPrev();
    const prefabIdx =
      findLastIndex(stepInfos.slice(0, step), (item) => {
        return !item.isPrefab && item.type !== StepTypeEnum.PART_ACTION;
      }) || 1;
    dispatch.jobConfig.stepTo(prefabIdx);
    let prevStepObj = stepInfos[prefabIdx];
    const stepObj = stepInfos[step];
    if (
      !prevStepObj.isPrefab &&
      prevStepObj.partSpec?.skuId !== stepObj.partSpec?.skuId
    ) {
      dispatch.jobConfig.setPartDropdownList(
        Array.from(
          new Set([
            ...partDropdownList.filter(
              (item) => item !== stepObj.partSpec?.skuId
            ),
            prevStepObj.partSpec?.skuId || 0,
          ])
        )
      );
    }
  });

  const nextClick = useMemoizedFn(async () => {
    dispatch.jobConfig.setRevxSpinloading(true);
    if (typeof stepAction === 'function') {
      try {
        const res = await stepAction();
        dispatch.jobConfig.setRevxSpinloading(false);
        if (res) {
          dispatch.jobConfig.goToNext();
        } else {
          return false;
        }
      } catch {
        dispatch.jobConfig.setRevxSpinloading(false);
      }
    } else {
      dispatch.jobConfig.goToNext();
      dispatch.jobConfig.setRevxSpinloading(false);
    }
    const prefabIdx =
      findIndex(stepInfos.slice(step + 1), (item) => {
        return !item.isPrefab && item.type !== StepTypeEnum.PART_ACTION;
      }) +
      step +
      1;
    dispatch.jobConfig.stepTo(prefabIdx);
    const nextStepObj = stepInfos[prefabIdx];
    const stepObj = stepInfos[step];
    if (
      !nextStepObj.isPrefab &&
      nextStepObj.partSpec?.skuId !== stepObj.partSpec?.skuId
    ) {
      dispatch.jobConfig.setPartDropdownList(
        Array.from(
          new Set([
            ...partDropdownList.filter(
              (item) => item !== stepObj.partSpec?.skuId
            ),
            nextStepObj.partSpec?.skuId || 0,
          ])
        )
      );
    }
  });

  return (
    <>
      <RevxButton
        brandGreen
        type="primary"
        className="back"
        disabled={prevDisabled}
        onClick={() => prevClick()}
      >
        <BtnBackIcon />
        BACK
      </RevxButton>
      <Tooltip title={nextBtnTip}>
        <RevxButton
          hidden={nextHide}
          brandGreen
          type="primary"
          className="next"
          disabled={nextDisabled}
          onClick={() => nextClick()}
        >
          NEXT
          <BtnNextIcon />
        </RevxButton>
      </Tooltip>
    </>
  );
};
